<template>
  <div class="user-select-none">
    <header>
      <dp-admin-navbar
        v-if="!!isAdmin"
        :is-logged-in="!!isAdmin"
        :base-url="adminBaseURL"
        :logo-url="`${getBaseUrl}/${logoPath}`"
        class="bg-white"
      >
        <template #nav-items>
          <b-link class="btn btn-outline-primary" :to="{ name: 'AdminRedirect' }" v-text="'Back to Order'" />
        </template>
      </dp-admin-navbar>
      <!-- Marketing Info -->
      <div
        v-if="$router.currentRoute.meta && $router.currentRoute.meta.showMarketingInfo && marketingInfo.info"
        :key="$router.currentRoute.name"
        class="d-flex align-items-center justify-content-center flex-gap-1 py-2 px-3"
        :style="{ backgroundColor: '#DEF6FF' }"
      >
        <b-img v-if="marketingInfo.img" :src="getImage(marketingInfo.img)" :alt="`${marketingInfo.info}`" width="80" fluid />
        <!-- eslint-disable-next-line vue/no-v-html (Make sure it's always hard-coded) -->
        <div v-html="marketingInfo.info" />
      </div>
      <plus-savings-banner v-if="showPlusSavingsBanner" @banner-state="onPlusBannerState" />
      <notification-container
        v-if="!isAdmin"
        :only-licensing-notifications="isLicensingFlow || productInterrupt || onWalletCardConfiguration"
        :suppressed-families="suppressedNotificationFamilies"
      />
    </header>
    <main id="main">
      <dp-container v-show="showLoading" id="loading-container" :data-loading="showLoading ? 'true' : 'false'">
        <dp-loading />
        <div v-if="refreshButton" class="text-center mt-5">
          <b-link class="btn btn-primary font-weight-bold" onclick="window.location.reload()">{{ $t('dp.productNotFound.buttons.refresh') }}</b-link>
        </div>
      </dp-container>
      <router-view v-show="!showLoading" @showSideBasket="$store.commit('basket/SHOW_BASKET', true)" />
      <side-basket
        v-if="!isAdmin && !isLicensingFlow"
        id="side-basket"
        :value="showSideBasket"
        @remove-pet-tracker="removeTrackerFromSideBasketClicked"
        @remove-bundle="removeBundleFromSideBasketClicked"
      />
    </main>
    <dp-footer ref="footer" :base-url="baseUrl" />
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import { DpAdminNavbar, DpContainer, DpFooter, DpLoading } from '@docupet/component-library';
import NotificationContainer from './components/library/NotificationContainer';
import PlusSavingsBanner from './components/PlusSavingsBanner';
import { NotificationMixins } from '@/mixins/notification';
import { CookieMixins } from '@/mixins/cookie';
import SideBasket from './components/SideBasket';
import getImage from '@/mixins/getImage';

export default {
  components: {
    SideBasket,
    DpAdminNavbar,
    DpFooter,
    DpContainer,
    DpLoading,
    NotificationContainer,
    PlusSavingsBanner,
  },
  mixins: [CookieMixins, NotificationMixins, getImage],
  data() {
    return {
      appLoading: false,
      refreshButton: false,
      initialLoadComplete: false,
      plusBannerState: { visible: false, variant: 'upsell', showsBasketAffordance: false },
    };
  },
  computed: {
    ...mapState({
      orderId: (state) => state.orderId,
      cityId: (state) => state.cityId,
      baseUrl: (state) => state.baseUrl,
      hasToken: (state) => !!state.token,
      loading: (state) => state.user.loading || state.user.cityLoading || state.order.loading,
      order: (state) => state.order.order,
      localItems: (state) => state.order.localItems,
      city: (state) => state.user.city,
      user: (state) => state.user.user,
      showSideBasket: (state) => state.basket.showBasket,
      isPetOwner: (state) => state.user.user.id && state.user.user.__typename == 'PetOwner',
      isAdmin: (state) => state.user.user.id && state.user.user.__typename == 'Administrator',
    }),
    ...mapGetters({
      allLicensingPetsHaveTagAssigned: 'order/isValid',
      getRequiredPetByPetId: 'order/getRequiredPetByPetId',
      localBasketCount: 'order/getLocalCount',
      generateFullUrl: 'generateFullUrl',
      getBaseUrl: 'getBaseUrl',
      getLocale: 'getLocale',
    }),
    showLoading() {
      return this.loading || this.appLoading;
    },
    showPlusSavingsBanner() {
      return !this.isAdmin && this.initialLoadComplete && !this.showLoading;
    },
    suppressedNotificationFamilies() {
      const { visible, showsBasketAffordance } = this.plusBannerState;

      return visible && showsBasketAffordance ? ['AddItem'] : [];
    },
    requiredPet() {
      const petId = this.$router?.currentRoute?.params?.petId;
      return this.getRequiredPetByPetId(
        petId,
        !(this.isAdmin && (this.$router?.currentRoute?.query?.single || this.$router?.currentRoute?.query?.['replacement-tag']))
      );
    },
    logoPath() {
      if (this.city && this.city.logo) {
        return this.city.logo.original;
      } else if (this.city && this.city.branding && this.city.branding.logoImage) {
        return this.city.branding.logoImage.original;
      } else {
        return '';
      }
    },
    adminBaseURL() {
      return window.APP_PATH.split('/store')[0];
    },
    isLicensingFlow() {
      return this.requiredPet?.licensing && !this.requiredPet?.hasTag;
    },
    productInterrupt() {
      return this.$router.currentRoute.name === 'ProductInterrupt';
    },
    onWalletCardConfiguration() {
      return this.$router.currentRoute.name === 'WalletCardStyle';
    },
    marketingInfo() {
      return !this.isAdmin
        ? !this.isLicensingFlow
          ? { info: this.city?.marketingCopy, img: this.city?.marketingCopyImage?.original }
          : { info: this.city?.marketingLicensingCopy, img: this.city?.marketingLicensingCopyImage?.original }
        : { info: this.city?.marketingAdminCopy, img: this.city?.marketingAdminCopyImage?.original };
    },
  },
  watch: {
    order() {
      let assign = false;
      if (this.order && this.order.items) {
        this.order.items.forEach((item) => {
          if (
            item.product &&
            !item.parent &&
            (['DESIGNER_TAG', 'PET_TAG'].includes(item.product.type) ||
              (['COLLAR'].includes(item.product.type) && item.customTagOrderInfo?.id && item.customTagOrderInfo?.phone)) &&
            item.group &&
            !item.group.pet
          ) {
            assign = true;
          }
        });
        // Check if order has designer tag before applying promo code
        const code = this.getBasketCodeCookie();

        if (code && !assign && this.orderId) {
          this.applyLocalDiscount(code);
        }
      }
      if (assign) this.$router.push({ name: 'AssignProduct', query: { ...this.$router.currentRoute.query } }).catch(() => {});
    },
    requiredPet(newVal, oldVal) {
      if (!newVal && oldVal && oldVal.id) {
        this.appLoading = true;
        if (this.isAdmin) {
          this.$router.push({ name: 'AdminRedirect' });
        } else {
          this.appLoading = false;
        }
      }
    },
    showLoading(value) {
      if (value === true) {
        setTimeout(() => {
          if (this.showLoading) {
            this.refreshButton = true;
          }
        }, 15000);
      } else {
        this.refreshButton = false;
      }
    },
  },
  async created() {
    try {
      await this.loadInitialState();
    } finally {
      this.initialLoadComplete = true;
    }
  },
  methods: {
    async loadInitialState() {
      let data = await this.splitSetBasket();
      await this.$store.dispatch('user/fetchCity', { vm: this, cityId: this.cityId });
      if (this.hasToken && this.orderId) {
        await this.$store.dispatch('user/fetchUser', { vm: this });
        if ((data?.length || 0) === 0) {
          await this.$store.dispatch('order/fetchOrder', { vm: this, orderId: this.orderId });
        } else {
          let items = [];
          data.forEach((productDetails) => {
            items.push({
              productId: productDetails.productId,
              children: productDetails.connector
                ? [
                    ...(!productDetails.isSplitRingConnector
                      ? [
                          {
                            productId: productDetails.connector,
                          },
                        ]
                      : []),
                    ...productDetails.otherProductIds
                      .filter((productId) => productId)
                      .map((productId) => ({
                        productId,
                      })),
                  ]
                : null,
              petId: productDetails?.petId || null,
              personalization:
                productDetails.personalize?.selected && productDetails.personalize?.valid
                  ? { name: productDetails.personalize?.name || '', phone: productDetails.personalize?.phone || '' }
                  : null,
              designExports: productDetails.designExports
                ? {
                    tagFront: productDetails.designExports?.tagFront,
                    laserFront: productDetails.designExports?.laserFront,
                  }
                : null,
              keyChain: productDetails.keyChain,
            });
          });
          this.$store
            .dispatch('order/addLocalItems', {
              vm: this,
              orderId: this.orderId,
              items,
            })
            .then(() => this.removeBasketCookie());
        }
      } else {
        if (data == null) {
          data = [];
        }
        await this.$store.dispatch('order/storeLocalItems', { data });
      }
    },
    onPlusBannerState(state) {
      this.plusBannerState = state;
    },
    applyLocalDiscount(code) {
      // Apply promo Code
      if (code) {
        this.$store.dispatch('basket/applyDiscount', { orderId: this.orderId, code }).then(() => this.removeBasketCodeCookie());
      }
    },
    removeTrackerFromSideBasketClicked() {
      window.dataLayer.push({
        event: 'pet_tracking_side_basket_click_remove',
      });
    },
    removeBundleFromSideBasketClicked() {
      window.dataLayer.push({
        event: 'side_basket_order_bundle_item_remove',
      });
    },
  },
};
</script>

<style lang="scss">
@import '@docupet/component-library/src/assets/styles/main';

.flex-gap-1 {
  gap: 1rem !important;
}

.no-stock {
  cursor: not-allowed !important;
  position: relative;
  > * {
    pointer-events: none;
  }

  svg {
    width: 80%;
    height: 80%;
    position: absolute;
    top: 10%;
    left: 10%;

    line {
      stroke: $dp-gray-3;
      stroke-width: 4;
    }
  }
  ~ .copy {
    line-height: normal;
    margin: 0.15rem 0;
  }
}

.text-brand-primary {
  color: #0D7BC5;
}

.text-brand-dark {
  color: #003C71;
}

.btn-dplight {
  color: #fff !important;
  background-color: #0D7BC5 !important;
  border-color: #0D7BC5 !important;
}
</style>

<!-- Override Styles - TEMP -->
<style lang="css">
.dp-container {
  border-radius: 0 !important;
}
</style>
